import React, { useEffect, useState } from 'react';
import { Box, Group, Text, ActionIcon, Stack } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconDeviceFloppy, IconTrash } from '@tabler/icons-react';
import type {
  Location,
  MembershipVisit,
  MembershipVisitRequest,
} from '../../types';
import { LocationSearchSelect } from './LocationSearchSelect';
import { nightsBetween } from '../../utils/trips/nightsBetween';
import {
  buildMembershipVisitRequest,
  type MembershipVisitFormState,
} from '../../utils/trips/buildMembershipVisitRequest';
import { validateMembershipVisit } from '../../utils/validation/membershipVisitValidation';
import { toMantineDateString } from '../../utils/mantineDateUtils';
import { useLocale } from '../../contexts/LocaleContext';
import { useLocaleDateFormatter } from '../../hooks/useLocaleDateFormatter';

interface MembershipVisitDateFormState {
  startDate: string;
  endDate: string;
}

function toMembershipVisitFormState(
  dateForm: MembershipVisitDateFormState,
  location: Location | null
): MembershipVisitFormState {
  return {
    ...dateForm,
    locationId: location?.id ?? null,
  };
}

export interface MembershipVisitRowProps {
  visit?: MembershipVisit;
  itineraryVisits?: MembershipVisit[];
  color: string;
  readOnly: boolean;
  onSave: (body: MembershipVisitRequest) => void;
  onDelete?: () => void;
  isSaving?: boolean;
}

export const MembershipVisitRow: React.FC<MembershipVisitRowProps> = ({
  visit,
  itineraryVisits = [],
  color,
  readOnly,
  onSave,
  onDelete,
  isSaving = false,
}) => {
  const { locale } = useLocale();
  const { formatDisplayDate } = useLocaleDateFormatter();
  const [dateForm, setDateForm] = useState<MembershipVisitDateFormState>(
    () => ({
      startDate: visit?.startDate
        ? toMantineDateString(visit.startDate, locale)
        : '',
      endDate: visit?.endDate ? toMantineDateString(visit.endDate, locale) : '',
    })
  );
  const [selectedLocation, setSelectedLocation] = useState(
    visit?.location ?? null
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!visit) {
      return;
    }
    setDateForm({
      startDate: visit.startDate
        ? toMantineDateString(visit.startDate, locale)
        : '',
      endDate: visit.endDate ? toMantineDateString(visit.endDate, locale) : '',
    });
    setSelectedLocation(visit.location);
  }, [visit, locale]);

  const nights = nightsBetween(dateForm.startDate, dateForm.endDate);

  const handleSave = () => {
    const formState = toMembershipVisitFormState(dateForm, selectedLocation);
    const { errors: validationErrors, isValid } = validateMembershipVisit(
      formState,
      {
        visitId: visit?.id,
        itineraryVisits: itineraryVisits.map(itineraryVisit => ({
          id: itineraryVisit.id,
          sortOrder: itineraryVisit.sortOrder,
          startDate: itineraryVisit.startDate,
          endDate: itineraryVisit.endDate,
        })),
      }
    );
    if (!isValid) {
      setErrors(validationErrors as Record<string, string>);
      return;
    }
    setErrors({});
    const request = buildMembershipVisitRequest(formState);
    if (!request) {
      return;
    }
    onSave(request);
  };

  if (readOnly && visit) {
    return (
      <Box
        data-cy={`membership-visit-row-${visit.id}`}
        style={{
          borderLeft: `4px solid ${color}`,
          paddingLeft: 12,
          marginBottom: 8,
        }}
      >
        <Group justify="space-between" wrap="nowrap">
          <Stack gap={4}>
            <Text fw={500}>{visit.location.displayName}</Text>
            <Text size="sm" c="dimmed">
              {formatDisplayDate(visit.startDate)} →{' '}
              {formatDisplayDate(visit.endDate)}
              {visit.startDate && visit.endDate
                ? ` · ${nightsBetween(visit.startDate, visit.endDate)} nights`
                : ''}
            </Text>
          </Stack>
        </Group>
      </Box>
    );
  }

  return (
    <Box
      data-cy={
        visit ? `membership-visit-row-${visit.id}` : 'membership-visit-row-new'
      }
      style={{
        borderLeft: `4px solid ${color}`,
        paddingLeft: 12,
        marginBottom: 12,
      }}
    >
      <Group gap="xs" align="flex-end" wrap="nowrap">
        <Box style={{ flex: '2 1 160px', minWidth: 0 }}>
          <LocationSearchSelect
            value={selectedLocation}
            onChange={loc => {
              setSelectedLocation(loc);
              if (errors.locationId) {
                setErrors(prev => ({ ...prev, locationId: '' }));
              }
            }}
            disabled={isSaving}
            error={errors.locationId}
          />
        </Box>
        <Box style={{ flex: '1 1 120px', minWidth: 0 }}>
          <DatePickerInput
            label="Arrival"
            value={dateForm.startDate}
            onChange={val => {
              setDateForm(prev => ({
                ...prev,
                startDate: val ?? '',
              }));
              if (errors.startDate) {
                setErrors(prev => ({ ...prev, startDate: '' }));
              }
            }}
            disabled={isSaving}
            error={errors.startDate}
            data-cy="membership-visit-start"
          />
        </Box>
        <Box style={{ flex: '1 1 120px', minWidth: 0 }}>
          <DatePickerInput
            label="Departure"
            value={dateForm.endDate}
            onChange={val => {
              setDateForm(prev => ({
                ...prev,
                endDate: val ?? '',
              }));
              if (errors.endDate) {
                setErrors(prev => ({ ...prev, endDate: '' }));
              }
            }}
            disabled={isSaving}
            error={errors.endDate}
            data-cy="membership-visit-end"
          />
        </Box>
        <Stack
          gap={0}
          align="center"
          style={{ flexShrink: 0, paddingBottom: 4 }}
        >
          {nights > 0 ? (
            <>
              <Text size="sm" fw={500}>
                {nights}
              </Text>
              <Text size="xs" c="dimmed">
                night{nights === 1 ? '' : 's'}
              </Text>
            </>
          ) : (
            <Text size="sm" c="dimmed">
              —
            </Text>
          )}
        </Stack>
        <Group gap="xs" style={{ flexShrink: 0 }}>
          <ActionIcon
            variant="subtle"
            aria-label="Save membership visit"
            onClick={handleSave}
            loading={isSaving}
            data-cy="membership-visit-save"
          >
            <IconDeviceFloppy size={16} />
          </ActionIcon>
          {onDelete && (
            <ActionIcon
              variant="subtle"
              color="red"
              aria-label="Delete membership visit"
              onClick={onDelete}
              disabled={isSaving}
              data-cy="membership-visit-delete"
            >
              <IconTrash size={16} />
            </ActionIcon>
          )}
        </Group>
      </Group>
    </Box>
  );
};

export default MembershipVisitRow;
